import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, FileText, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DOC_TYPE_OPTIONS = [
  ["insurance_card", "Insurance Card"],
  ["license_front", "Driver's License (Front)"],
  ["license_back", "Driver's License (Back)"],
  ["selfie", "Selfie Verification"],
  ["additional_driver", "Additional Driver's License"],
  ["other", "Other Document…"],
];

export function DocFile({ path, label, token, sub }) {
  const [url, setUrl] = useState(null);
  const [pdf, setPdf] = useState(false);
  useEffect(() => {
    if (!path) return;
    let live = true, obj;
    axios.get(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((res) => {
        if (live) {
          setPdf(res.data.type === "application/pdf" || path.endsWith(".pdf"));
          obj = URL.createObjectURL(res.data);
          setUrl(obj);
        }
      }).catch(() => {});
    return () => { live = false; if (obj) URL.revokeObjectURL(obj); };
  }, [path, token]);
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" className="block w-28" title={label} data-testid={`doc-file-${label?.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="h-20 w-28 rounded-md border border-white/15 overflow-hidden bg-black flex items-center justify-center">
        {!url ? <Loader2 size={14} className="animate-spin text-white/30" />
          : pdf ? <span className="flex flex-col items-center gap-1 text-red-300 text-[10px] font-bold"><FileText size={22} /> PDF</span>
          : <img src={url} alt={label} className="w-full h-full object-cover" />}
      </div>
      <span className="text-[10px] text-white/50 mt-1 block text-center truncate">{label}</span>
      {sub && <span className="text-[9px] text-white/30 block text-center truncate">{sub}</span>}
    </a>
  );
}

export default function AdminDocUpload({ entityType, entityId, token, onUploaded }) {
  const fileRef = useRef(null);
  const [docType, setDocType] = useState("insurance_card");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const copyLink = async () => {
    setLinkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin-docs/upload-link`,
        { entity_type: entityType, entity_id: entityId },
        { headers: { Authorization: `Bearer ${token}` } });
      const url = data.url.startsWith("http") ? data.url : `${window.location.origin}${data.url}`;
      try { await navigator.clipboard.writeText(url); toast.success("Upload link copied — text or email it to the customer."); }
      catch { window.prompt("Copy this upload link:", url); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the link."); }
    finally { setLinkBusy(false); }
  };
  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file first.");
    if (docType === "other" && !label.trim()) return toast.error("Give the document a label (e.g. SR-22, Proof of address).");
    const fd = new FormData();
    fd.append("entity_type", entityType);
    fd.append("entity_id", entityId);
    fd.append("doc_type", docType);
    fd.append("label", label);
    fd.append("file", f);
    setBusy(true);
    try {
      await axios.post(`${API}/admin-docs/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${docType === "other" ? label : DOC_TYPE_OPTIONS.find(([k]) => k === docType)?.[1]} uploaded.`);
      if (fileRef.current) fileRef.current.value = "";
      setLabel("");
      onUploaded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={`admin-doc-upload-${entityType}-${entityId}`}>
      <label className="text-[10px] text-white/45 uppercase tracking-wider">Document
        <select data-testid="doc-upload-type" value={docType} onChange={(e) => setDocType(e.target.value)}
          className="block mt-1 bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs normal-case">
          {DOC_TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      {docType === "other" && (
        <label className="text-[10px] text-white/45 uppercase tracking-wider">Label
          <input data-testid="doc-upload-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. SR-22, Proof of address"
            className="block mt-1 bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs w-44 normal-case" />
        </label>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" data-testid="doc-upload-file"
        className="text-xs text-white/60 max-w-[210px] file:mr-2 file:px-2.5 file:py-1.5 file:rounded file:border-0 file:bg-white/10 file:text-white/80 file:text-xs file:cursor-pointer" />
      <button data-testid="doc-upload-btn" onClick={upload} disabled={busy}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F2B705]/15 border border-[#F2B705]/30 text-[#F2B705] text-xs font-bold hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
      </button>
      <button data-testid="doc-copy-link-btn" onClick={copyLink} disabled={linkBusy}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/20 text-white/75 text-xs font-bold hover:border-[#F2B705] hover:text-[#F2B705] transition-colors disabled:opacity-50">
        {linkBusy ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Copy Upload Link
      </button>
    </div>
  );
}
